import { useState, useEffect } from 'react'
import './AuditLogModal.css'
import { exportExcel } from '@/utils/spreadsheetHelpers'
import { DEVICE_AUDIT_TEMPLATE } from '@/constants/templateSchema'
import { Device } from '@/models/device'
import { AuditLog, PartnerAuditLogService } from '@/services/PartnerAuditLogService'

interface AuditLogModalProps {
  entityName: string
  entityId: string
  entityDisplayName: string
  device?: Device
  onClose: () => void
}

export default function AuditLogModal({ entityName, entityId, entityDisplayName, device, onClose }: AuditLogModalProps) {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchAuditLogs = async () => {
      try {
        setLoading(true)
        setError(null)
        const result = await PartnerAuditLogService.getAll(entityName, entityId)
        setAuditLogs(result.logs)
      } catch (err) {
        console.error('Failed to fetch audit logs:', err)
        setError('Failed to load audit logs')
      } finally {
        setLoading(false)
      }
    }

    fetchAuditLogs()
  }, [entityName, entityId])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  const parseJsonSafely = (jsonString: string | null | undefined): Record<string, unknown> | null => {
    if (!jsonString) return null
    try {
      return JSON.parse(jsonString)
    } catch {
      return null
    }
  }

  const formatDateForCSV = (dateString: string) => {
    const date = new Date(dateString)
    const day = String(date.getUTCDate()).padStart(2, '0')
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const year = date.getUTCFullYear()
    return `${day}/${month}/${year}`
  }

  const formatTimeForCSV = (dateString: string) => {
    const date = new Date(dateString)
    return date.toISOString().substring(11, 19) + ' UTC'
  }

  const exportToExcel = async () => {
    if (!device || entityName !== 'Device') {
      alert('CSV export is only available for devices')
      return
    }

    const csvRows: string[][] = []

    auditLogs.forEach((log) => {
      const changedProps = parseJsonSafely(log.changedProperties) as string[] | null
      const oldValues = parseJsonSafely(log.oldValues)
      const newValues = parseJsonSafely(log.newValues)

      // For Created/Deleted actions or when no specific fields changed
      if (!changedProps || changedProps.length === 0) {
        if (log.action === 'Created' && newValues) {
          // Add a row for the entity creation
          const row = [
            device.serialNumber ? device.serialNumber : '',
            device.imei ? device.imei : '',
            'Entity Created',
            'N/A',
            'Device created with initial values',
            formatDateForCSV(log.changedAtUtc),
            formatTimeForCSV(log.changedAtUtc),
            log.changedBy || 'System'
          ]
          csvRows.push(row)
        } else if (log.action === 'Deleted' && oldValues) {
          const row = [
            device.serialNumber ? device.serialNumber : '',
            device.imei ? device.imei : '',
            'Entity Deleted',
            'Entity existed',
            'Entity deleted',
            formatDateForCSV(log.changedAtUtc),
            formatTimeForCSV(log.changedAtUtc),
            log.changedBy || 'System'
          ]
          csvRows.push(row)
        }
        return
      }

      changedProps.forEach((prop) => {
        const oldVal = oldValues?.[prop]
        const newVal = newValues?.[prop]
        const row = [
          device.serialNumber ? device.serialNumber : '',
          device.imei ? device.imei : '',
          prop,
          oldVal !== null && oldVal !== undefined ? String(oldVal) : 'N/A',
          newVal !== null && newVal !== undefined ? String(newVal) : 'N/A',
          formatDateForCSV(log.changedAtUtc),
          formatTimeForCSV(log.changedAtUtc),
          log.changedBy || 'System'
        ]
        csvRows.push(row)
      })
    })
  
    exportExcel(DEVICE_AUDIT_TEMPLATE.name, DEVICE_AUDIT_TEMPLATE.columns, csvRows);
  }

  const renderChanges = (log: AuditLog) => {
    const changedProps = parseJsonSafely(log.changedProperties) as string[] | null
    const oldValues = parseJsonSafely(log.oldValues)
    const newValues = parseJsonSafely(log.newValues)

    if (!changedProps || changedProps.length === 0) {
      if (log.action === 'Created' && newValues) {
        return (
          <div className="audit-changes">
            <strong>New values:</strong>
            <ul>
              {Object.entries(newValues).map(([key, value]) => (
                <li key={key}>
                  <span className="field-name">{key}:</span> {String(value)}
                </li>
              ))}
            </ul>
          </div>
        )
      }
      if (log.action === 'Deleted' && oldValues) {
        return (
          <div className="audit-changes">
            <strong>Deleted values:</strong>
            <ul>
              {Object.entries(oldValues).map(([key, value]) => (
                <li key={key}>
                  <span className="field-name">{key}:</span> {String(value)}
                </li>
              ))}
            </ul>
          </div>
        )
      }
      return <span className="no-changes">No changes recorded</span>
    }

    return (
      <div className="audit-changes">
        <strong>Changed fields:</strong>
        <ul>
          {changedProps.map((prop) => {
            const oldVal = oldValues?.[prop]
            const newVal = newValues?.[prop]
            return (
              <li key={prop}>
                <span className="field-name">{prop}:</span>{' '}
                <span className="old-value">{oldVal !== null && oldVal !== undefined ? String(oldVal) : '(empty)'}</span>
                {' → '}
                <span className="new-value">{newVal !== null && newVal !== undefined ? String(newVal) : '(empty)'}</span>
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content audit-log-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Audit History: {entityDisplayName}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {loading && <div className="loading-message">Loading audit logs...</div>}
          {error && <div className="error-message">Failed to load audit logs</div>}

          {!loading && !error && auditLogs.length === 0 && (
            <div className="no-logs-message">No audit logs found.</div>
          )}

          {!loading && !error && auditLogs.length > 0 && (
            <div className="audit-logs-container">
              {auditLogs.map((log) => (
                <div key={log.id} className="audit-log-entry">
                  <div className="audit-log-header">
                    <span className={`audit-action action-${log.action.toLowerCase()}`}>
                      {log.action}
                    </span>
                    <span className="audit-timestamp">{formatDate(log.changedAtUtc)}</span>
                    <span className="audit-user">by {log.changedBy || 'System'}</span>
                  </div>
                  <div className="audit-log-details">
                    {renderChanges(log)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {device && entityName === 'Device' && auditLogs.length > 0 && (
            <button className="export-button" onClick={exportToExcel}>
              Export to CSV
            </button>
          )}
          <button className="cancel-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
